import React from "react";
import { useData } from "../lib/store.jsx";
import {
  Kpi, Panel, Bars, Donut, RadarPanel, DualBarLine,
} from "../components/ui.jsx";
import { gbp0, gbpM, gbpCompact, pct1, BENCH } from "../lib/data.js";

const benchBadge = (v, b) => {
  if (v == null || !b) return null;
  const p = ((v - b) / b) * 100;
  return { kind: p >= 0 ? "up" : "down",
    text: `${p >= 0 ? "▲" : "▼"} ${Math.abs(p).toFixed(0)}%` };
};

export default function Financial() {
  const { d, m } = useData();

  const annual = m.annual
    .filter((a) => a.revenue != null)
    .map((a) => ({ x: a.fy, EBITDA: Math.round(a.ebitda),
      Revenue: Math.round(a.revenue), ytd: a.ytd }));

  const margin = m.annual
    .filter((a) => a.ebitdaMarginPct != null)
    .map((a) => ({ x: a.fy,
      "EBITDA margin %": +a.ebitdaMarginPct.toFixed(1) }));

  // Per-bed vs LaingBuisson, indexed (benchmark = 100).
  const pb = m.pbLatest || {};
  const radar = [
    ["Revenue / bed", pb.revPerBed, BENCH.revPerBed],
    ["Gross profit / bed", pb.gpPerBed, BENCH.gpPerBed],
    ["EBITDA / bed", pb.ebitdaPerBed, BENCH.ebitdaPerBed],
    ["EBITDA margin", pb.ebitdaMarginPct, BENCH.ebitdaMarginPct],
    ["Occupancy %", m.occYtdPct, 85],
  ].map(([metric, v, b]) => ({
    metric,
    Aria: v == null || !b ? 0 : +((v / b) * 100).toFixed(0),
    Benchmark: 100,
  }));

  const cagr = [...d.annualRev]
    .sort((a, b) => a.year - b.year)
    .map((r) => ({ x: String(r.year), Revenue: Math.round(+r.revenue) }));

  const mix = m.revenueMix.map((r) => ({
    name: r.name, value: Math.round(r.weekly) }));

  return (
    <>
        <Kpi label="MRR (12-month avg)" color="#c8e64a" value={m.mrrYearAvg} fmt={gbpCompact}
          meta="average monthly revenue, last 12 months" />
        <Kpi label="ARR" color="#f3ece2" value={m.arr} fmt={gbpCompact} meta="12-month average MRR × 12" />
        <Kpi label="EBITDA / Bed" color="#d9b892" value={m.ebitdaPerBed || 0} fmt={gbpCompact}
          badge={benchBadge(m.ebitdaPerBed, BENCH.ebitdaPerBed)}
          meta={`benchmark £${BENCH.ebitdaPerBed.toLocaleString("en-GB")}`} />
        <Kpi label="Revenue Growth" color="#ec9a74"
          value={m.revYoYPct == null ? 0 : m.revYoYPct} fmt={pct1}
          badge={m.revYoYPct == null ? null : {
            kind: m.revYoYPct >= 0 ? "up" : "down",
            text: `${m.revYoYPct >= 0 ? "▲" : "▼"}` }}
          meta={`${m.revYoYYear} vs ${m.revYoYYear - 1}, year on year`} />

        <Panel className="s7" title="Financial performance"
          sub="annual revenue and EBITDA by financial year">
          <DualBarLine data={annual} x="x" barKey="EBITDA" lineKey="Revenue"
            barName="EBITDA" lineName="Revenue" fmt={gbpM} h={300} />
        </Panel>
        <Panel className="s5" title="EBITDA margin vs sector benchmark"
          sub={`sector benchmark: ${BENCH.ebitdaMarginPct}% EBITDA margin`}>
          <Bars data={margin} x="x"
            bars={[{ key: "EBITDA margin %", name: "EBITDA margin %" }]}
            refLine={BENCH.ebitdaMarginPct} refLabel="23% sector"
            fmt={(v) => `${v}%`} h={300} />
        </Panel>
        <Panel className="s5" title="Per bed vs LaingBuisson">
          <RadarPanel data={radar} keys={[
            { key: "Aria", name: "Aria" },
            { key: "Benchmark", name: "Benchmark" }]} h={300} />
        </Panel>
        <Panel className="s7" title="Revenue growth (CAGR)"
          sub={m.cagrPct != null
            ? `${m.cagrPct.toFixed(1)}% CAGR · +${Math.round(m.totalGrowthPct)}`
              + "% total growth, 2015–2025"
            : "annual revenue, 2015–2025"}>
          <Bars data={cagr} x="x" bars={[{ key: "Revenue", name: "Revenue" }]}
            fmt={gbpM} h={300} />
        </Panel>

    </>
  );
}
